import type { AlertContentBlock, AlertMessage, AlertTextSegment } from 'components/feedback/alertMessageTypes';

export type ResetPasswordTextSegment = AlertTextSegment;
export type ResetPasswordContentBlock = AlertContentBlock;
export type ResetPasswordAlertCopy = AlertMessage;

export type ResetPasswordAlertKey =
    | 'expiredLinkError'
    | 'invalidCodeError'
    | 'missingLinkError'
    | 'passwordMismatchError'
    | 'passwordPolicyError'
    | 'resetSuccess'
    | 'defaultResetError';

export type ResetPasswordMessages = {
    alerts: Record<ResetPasswordAlertKey, ResetPasswordAlertCopy>;
};

export const defaultResetPasswordMessages: ResetPasswordMessages = {
    alerts: {
        missingLinkError: {
            title: "This link is broken — you won't be  able to reset your password",
            content: [
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'It may be misspelled or not working at all.',
                        },
                    ],
                },
                {
                    type: 'spacer',
                    size: 'small',
                },
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'Please try again accessing the link you have on your reset password e-mail — check your inbox.',
                        },
                    ],
                },
                {
                    type: 'spacer',
                    size: 'small',
                },
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: "If that doesn't work, please contact your system admin or the Control Center support team.",
                        },
                    ],
                },
            ],
        },
        invalidCodeError: {
            title: "This link is broken — you won't be  able to reset your password",
            content: [
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'The code in this link is invalid. Please try again accessing the link you have on your reset password email — check your inbox.',
                        },
                    ],
                },
                {
                    type: 'spacer',
                    size: 'small',
                },
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: "If that doesn't work, please contact your system admin or the Control Center support team.",
                        },
                    ],
                },
            ],
        },
        expiredLinkError: {
            title: "The link has been expired — you won't be able to reset your password",
            content: [
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'This link only works within 1 hour after the request is made.',
                        },
                    ],
                },
                {
                    type: 'spacer',
                    size: 'medium',
                },
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'To reset your password, you need to request it again from your system admin or the Control Center support team.',
                        },
                    ],
                },
            ],
        },
        passwordMismatchError: {
            title: 'Passwords do not match',
            content: [
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'The passwords you entered are different. Please make sure both fields contain the same password.',
                        },
                    ],
                },
            ],
        },
        passwordPolicyError: {
            title: 'Your password does not meet the requirements',
            content: [
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'Your password must be at least 8 characters and include at least one uppercase letter, one lowercase letter, one number, and one special character. Please update your password and try again.',
                        },
                    ],
                },
            ],
        },
        resetSuccess: {
            title: 'Your password has been reset successfully',
            content: [
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'Your new credentials can now be used to sign in to the Control Center.',
                        },
                    ],
                },
            ],
        },
        defaultResetError: {
            title: 'Sorry, something went wrong',
            content: [
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: "For technical reasons, we couldn't complete the action you tried to perform.",
                        },
                    ],
                },
                {
                    type: 'spacer',
                    size: 'medium',
                },
                {
                    type: 'paragraph',
                    segments: [
                        {
                            text: 'Please try again or refresh the page. If that does not work, please contact the Control Center support team.',
                        },
                    ],
                },
            ],
        },
    },
};
